'use client';

import { c as _c } from "react/compiler-runtime";
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { isAppModalHash } from '@src/utils/hash.util';

/** Strip app modal hashes on full page load so modals reopen via their own flows. */
const stripAppModalHashFromUrl = (): string => {
  if (typeof window === 'undefined') return '';
  const hash = window.location.hash.replace(/^#!?/, '');
  if (!isAppModalHash(hash)) return hash;
  window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
  return '';
};
const getCurrentHash = () => typeof window !== 'undefined' ? window.location.hash.replace(/^#!?/, '') : '';

/** Survives Strict Mode remounts — strip stale modal hashes only once per full page load. */
let hasStrippedStaleModalHashOnLoad = false;
type SetHashOptions = {
  /** Use replaceState instead of history.back() when clearing the hash. */
  navigation?: 'back' | 'replace';
};
export const useURLHashState = () => {
  const $ = _c(10);
  const pathname = usePathname();
  const [hash, _setHash] = useState("");
  const isInitialPathnameSync = useRef(true);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      if (hasStrippedStaleModalHashOnLoad) {
        return;
      }
      hasStrippedStaleModalHashOnLoad = true;
      _setHash(stripAppModalHashFromUrl());
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useLayoutEffect(t0, t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = function setHash(newHash, options) {
      _setHash(newHash);
      if (newHash) {
        history.pushState({
          useURLHashState: true
        }, "", `#${newHash}`);
      } else {
        if (options?.navigation === "replace") {
          history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
        } else {
          if (history.state?.useURLHashState) {
            history.back();
          } else {
            history.replaceState(null, "", window.location.pathname);
          }
        }
      }
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const setHash = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      if (isInitialPathnameSync.current) {
        isInitialPathnameSync.current = false;
        return;
      }
      _setHash(stripAppModalHashFromUrl());
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== pathname) {
    t4 = [pathname];
    $[4] = pathname;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      const handleHashChange = () => {
        const currentHash = getCurrentHash();
        _setHash(currentHash);
      };
      const {
        pushState,
        replaceState
      } = window.history;
      window.history.pushState = function (...t7) {
        const args = t7;
        pushState.apply(window.history, args);
        setTimeout(() => _setHash(getCurrentHash()));
      };
      window.history.replaceState = function (...t8) {
        const args_0 = t8;
        replaceState.apply(window.history, args_0);
        setTimeout(() => _setHash(getCurrentHash()));
      };
      window.addEventListener("hashchange", handleHashChange);
      return () => {
        window.removeEventListener("hashchange", handleHashChange);
      };
    };
    t6 = [];
    $[6] = t5;
    $[7] = t6;
  } else {
    t5 = $[6];
    t6 = $[7];
  }
  useEffect(t5, t6);
  let t7;
  if ($[8] !== hash) {
    t7 = [hash, setHash];
    $[8] = hash;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  return t7 as const;
};